/**
 * Phase H — `useFilteredTables` memoised selector.
 *
 * Applies the 3 independent filter dimensions (mode, search, categories)
 * + per-mode sort + section bucketing, in one place.
 *
 * Output shape (consumed by `TableGrid`):
 *   {
 *     mode:     null | 'Not Arrived' | 'Waiting' | 'Seated',
 *     sections: { 0: [...], 1: [...], 2: [...], 3: [...], 4: [...] },
 *     dynamic:  [...],
 *     flat:     [...]  // used by Waiting mode (single horizontal row)
 *   }
 *
 * Memoisation key: [tables, selectedMode, searchQuery,
 * activeCategoryFilters, breakfastStarted]. NOT `tick` — see C-17.
 *
 * Sort rules (see C-23, C-24 + doc 06 §5):
 *   - mode === 'Waiting' → sort by waiting_start_time ASC (skew-immune)
 *   - mode === 'Not Arrived' / 'Seated' → extractNumber ASC
 *   - mode === null → (statusPriority, extractNumber) ASC within section
 */
import { useMemo } from "react";
import useAppStore from "@/store/useAppStore";
import { formatRoomId, extractNumber } from "@/utils/formatRoomId";
import { bucketBySection } from "@/utils/sections";

const STATUS_PRIORITY = { available: 0, reserved: 1, occupied: 2 };

function matchesSearch(table, query) {
  if (!query) return true;
  const raw = query.trim();
  if (raw === "") return true;
  const rawLower = raw.toLowerCase();
  const normalisedQuery = formatRoomId(raw).toLowerCase();
  const code = (table.code || "").toLowerCase();
  const normalisedCode = formatRoomId(table.code).toLowerCase();
  return (
    normalisedCode.includes(normalisedQuery) || code.includes(rawLower)
  );
}

function matchesCategories(table, activeFilters, breakfastStarted) {
  if (!breakfastStarted) return true;
  if (!activeFilters || activeFilters.length === 0) return true;
  const cats = table.categories || [];
  for (const f of activeFilters) {
    if (cats.includes(f)) return true;
  }
  return false;
}

function matchesMode(table, mode) {
  if (mode === null || mode === undefined) return true;
  if (mode === "Not Arrived") return table.status === "available";
  if (mode === "Waiting") return table.status === "reserved";
  if (mode === "Seated") return table.status === "occupied";
  return true;
}

function compareWaiting(a, b) {
  // ASC by start time → oldest first → longest waiting on the left.
  const ta = a.waiting_start_time ? Date.parse(a.waiting_start_time) : Infinity;
  const tb = b.waiting_start_time ? Date.parse(b.waiting_start_time) : Infinity;
  if (ta !== tb) return ta - tb;
  // Tie-break: numeric, then lexicographic
  const na = extractNumber(a.code);
  const nb = extractNumber(b.code);
  if (na !== nb) return na - nb;
  return (a.code || "").localeCompare(b.code || "");
}

function compareNumeric(a, b) {
  return extractNumber(a.code) - extractNumber(b.code);
}

function compareAllMode(a, b) {
  // (statusPriority, extractNumber) ASC. Within each section, this means:
  // all available first, then reserved, then occupied; each cluster
  // ordered numerically. C-24.
  const pa = STATUS_PRIORITY[a.status] ?? 99;
  const pb = STATUS_PRIORITY[b.status] ?? 99;
  if (pa !== pb) return pa - pb;
  return compareNumeric(a, b);
}

export default function useFilteredTables() {
  const tables = useAppStore((s) => s.tables);
  const selectedMode = useAppStore((s) => s.selectedMode);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const activeCategoryFilters = useAppStore(
    (s) => s.appState?.active_category_filters
  );
  const breakfastStarted = useAppStore((s) => s.appState?.breakfast_started);

  return useMemo(() => {
    const list = Object.values(tables || {}).filter(
      (t) =>
        matchesMode(t, selectedMode) &&
        matchesSearch(t, searchQuery) &&
        matchesCategories(t, activeCategoryFilters, breakfastStarted)
    );

    // Choose comparator per mode.
    let comparator;
    if (selectedMode === "Waiting") comparator = compareWaiting;
    else if (selectedMode === "Not Arrived" || selectedMode === "Seated")
      comparator = compareNumeric;
    else comparator = compareAllMode;

    const sorted = [...list].sort(comparator);

    // Waiting mode renders a single horizontal row (no sections).
    if (selectedMode === "Waiting") {
      return {
        mode: selectedMode,
        sections: { 0: [], 1: [], 2: [], 3: [], 4: [] },
        dynamic: [],
        flat: sorted,
      };
    }

    const { sections, dynamic } = bucketBySection(sorted);

    return {
      mode: selectedMode ?? null,
      sections,
      dynamic,
      flat: sorted,
    };
  }, [
    tables,
    selectedMode,
    searchQuery,
    activeCategoryFilters,
    breakfastStarted,
  ]);
}
